import type { FC, ReactNode } from 'react';
import { useId } from 'react';
import { Tag, TagGroup, TagList } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import type { MessageMetaItem } from './MessageMeta';
import { MessageMeta } from './MessageMeta';

import {
  card,
  cardAgent,
  cardAgentChat,
  cardBelow,
  cardBelowEnd,
  cardBelowStart,
  cardBody,
  cardHeader,
  cardHeaderExtras,
  cardHeaderMain,
  cardUser,
  copyAffordance,
  modelChip,
  modelChipIcon,
  turn,
  turnAgent,
  turnAgentChat,
  turnUser,
} from '../../internal/components/conversation/ConversationChrome.css';

export type ConversationMessageSpeaker = 'user' | 'agent';
export type ConversationChromeVariant = 'transcript' | 'chat';

export interface ConversationMessageCardProps {
  speaker: ConversationMessageSpeaker;
  /**
   * `transcript` (default) is the Read-transcript chrome: User/Agent tags and
   * a bordered agent card. `chat` is Vega-only: no role tags, flush agent replies.
   */
  variant?: ConversationChromeVariant;
  /** Secondary chip on agent messages (chip-ai + model id). */
  model?: string;
  children: ReactNode;
  /** Non-interactive chips (e.g. eval badges) in the header. */
  badges?: ReactNode;
  /** Sibling controls outside the role/model header (e.g. span icon buttons). */
  headerExtra?: ReactNode;
  /** Copy affordance (or similar) — shares the caption row with meta. */
  footer?: ReactNode;
  meta?: MessageMetaItem[];
  metaTitle?: string;
  className?: string;
  /** Hide the caption row until the turn is hovered or focused. Pair with `copyHoverGroup` on `className`. */
  hoverRevealCaption?: boolean;
}

const SPEAKER_TAG: Record<ConversationMessageSpeaker, { icon: 'person-outline' | 'robot'; label: string }> = {
  user: { icon: 'person-outline', label: 'User' },
  agent: { icon: 'robot', label: 'Agent' },
};

/**
 * Shared user/agent message chrome: role Tag, optional model chip, body, optional
 * footer, and quiet meta caption. Indent and surface follow the conversation Figma.
 * Not selectable — span drill-in and Inspect use their own controls.
 */
export const ConversationMessageCard: FC<ConversationMessageCardProps> = ({
  speaker,
  variant = 'transcript',
  model,
  children,
  badges,
  headerExtra,
  footer,
  meta,
  metaTitle,
  className,
  hoverRevealCaption,
}) => {
  const roleId = useId();
  const tag = SPEAKER_TAG[speaker];
  const isChat = variant === 'chat';
  const turnClass = speaker === 'user' ? turnUser : isChat ? turnAgentChat : turnAgent;
  const cardClass = speaker === 'user' ? cardUser : isChat ? cardAgentChat : cardAgent;
  const hasMeta = (meta != null && meta.length > 0) || Boolean(metaTitle);
  const showCaption = Boolean(footer) || hasMeta;
  const showModel = speaker === 'agent' && Boolean(model);
  const showHeader = !isChat || showModel || Boolean(badges) || Boolean(headerExtra);

  return (
    <div className={[turn, turnClass, className].filter(Boolean).join(' ')}>
      <div className={[card, cardClass].join(' ')}>
        {showHeader ? (
          <div className={cardHeader}>
            <div className={cardHeaderMain}>
              {!isChat ? (
                <TagGroup aria-label={tag.label}>
                  <TagList>
                    <Tag size="small" id={roleId}>
                      <Icon name={tag.icon} size="tiny" />
                      {tag.label}
                    </Tag>
                  </TagList>
                </TagGroup>
              ) : null}
              {showModel ? (
                <span className={modelChip} title={model}>
                  <Icon name="chip-ai" size="tiny" className={modelChipIcon} />
                  {model}
                </span>
              ) : null}
              {badges}
            </div>
            {headerExtra ? <div className={cardHeaderExtras}>{headerExtra}</div> : null}
          </div>
        ) : null}
        <div className={cardBody}>{children}</div>
      </div>
      {showCaption ? (
        <div
          className={[
            cardBelow,
            speaker === 'user' ? cardBelowEnd : cardBelowStart,
            hoverRevealCaption ? copyAffordance : undefined,
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {speaker === 'agent' ? footer : null}
          {hasMeta ? <MessageMeta items={meta ?? []} title={metaTitle} /> : null}
          {speaker === 'user' ? footer : null}
        </div>
      ) : null}
    </div>
  );
};
